import { KeyboardEvent as ReactKeyboardEvent, RefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Deliberately not an `offsetParent` check: jsdom lays nothing out, so that would report every
 * control in a test as unreachable.
 */
const isReachable = (element: HTMLElement): boolean =>
  !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true';

const tabbableWithin = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isReachable);

let dialogsByOpenOrder: RefObject<HTMLDivElement>[] = [];

const isTopmostOpenDialog = (dialog: RefObject<HTMLDivElement>): boolean =>
  dialogsByOpenOrder[dialogsByOpenOrder.length - 1] === dialog;

interface ModalKeyboardParams {
  isShowing: boolean;
  onEscape?: () => void;
}

interface ModalKeyboard {
  dialogRef: RefObject<HTMLDivElement>;
  onDialogKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

/**
 * Gives a modal the keyboard behaviour a dialog is expected to have: it takes focus when it opens,
 * keeps Tab inside it, hands focus back to whatever opened it, and closes on Escape.
 *
 * Focus starts on the dialog itself rather than its first control, because that control is usually
 * the close button — landing there would make Enter dismiss the modal the instant it appears.
 *
 * @returns the ref to attach to the dialog element, which must be focusable (`tabIndex={-1}`), and
 *   the `onKeyDown` to put on that same element.
 */
export const useModalKeyboard = ({ isShowing, onEscape }: ModalKeyboardParams): ModalKeyboard => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const focusedBeforeOpen = useRef<HTMLElement | null>(null);
  const escape = useRef(onEscape);

  useEffect(() => {
    escape.current = onEscape;
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!isShowing || dialog === null) {
      return undefined;
    }

    focusedBeforeOpen.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.focus({ preventScroll: true });

    return () => {
      const previous = focusedBeforeOpen.current;
      if (previous !== null && document.body.contains(previous)) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [isShowing]);

  useEffect(() => {
    if (!isShowing) {
      return undefined;
    }

    dialogsByOpenOrder = [...dialogsByOpenOrder, dialogRef];

    const wrapFocus = (event: KeyboardEvent, dialog: HTMLDivElement) => {
      const tabbable = tabbableWithin(dialog);
      if (tabbable.length === 0) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = tabbable[0];
      const last = tabbable[tabbable.length - 1];
      const active = document.activeElement;
      const atStart = active === first || (active === dialog && event.shiftKey);
      const atEnd = active === last || (active === dialog && !event.shiftKey);

      if (!dialog.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && atStart) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && atEnd) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopmostOpenDialog(dialogRef)) {
        return;
      }
      if (event.key === 'Escape') {
        if (!event.defaultPrevented) {
          event.preventDefault();
          escape.current?.();
        }
        return;
      }
      const dialog = dialogRef.current;
      if (event.key === 'Tab' && dialog !== null) {
        wrapFocus(event, dialog);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      dialogsByOpenOrder = dialogsByOpenOrder.filter((open) => open !== dialogRef);
    };
  }, [isShowing]);

  const onDialogKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented || !isTopmostOpenDialog(dialogRef)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    escape.current?.();
  };

  return { dialogRef, onDialogKeyDown };
};
